import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { ApiResponse } from "@/api/rest/core/types";
import { WarrantyAutoReplyConfiguration } from "@/api/rest/models/configurations";
import {
  EntityConfiguration,
  EntityConfigurationEntityType,
  EntityConfigurationType,
  useUpdateConfiguration,
} from "@/api/rest/services/configurationService";
import { UserInfo } from "@/api/rest/models/account";
import { ApiBanner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import Field from "@/components/common/Field";
import Input from "@/components/common/Input";
import TextArea from "@/components/common/TextArea";
import { isDobuEmployee } from "@/utils/userUtils";
import Checkbox from "@/components/common/Checkbox";

interface WarrantySettingsProps {
  buildingConfiguration: EntityConfiguration[];
  buildingId: number;
  primaryLanguage: string;
  supportedLanguages: string[];
  refreshBuildingConfiguration: () => void;
}

type WarrantyNotificationFormData = {
  [EntityConfigurationType.WARRANTY_PROMPT]: string;
  [EntityConfigurationType.FILE_UPLOAD_MANDATORY]: boolean;
  [EntityConfigurationType.WARRANTY_DISABLED_MESSAGE]: string;
  [EntityConfigurationType.WARRANTY_SYNC_EMAIL]: string;
  [EntityConfigurationType.CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE]: string;
  [EntityConfigurationType.WARRANTY_EMAIL_USER_LIST]: string;
};

type FieldErrorState = Set<EntityConfigurationType>;

const getAutoReplyLanguages = (primaryLanguage: string, supportedLanguages: string[]) => {
  return Array.from(new Set([primaryLanguage, ...supportedLanguages].filter(Boolean)));
};

const parseAutoReplyConfiguration = (
  value: string,
  primaryLanguage: string,
  supportedLanguages: string[],
): WarrantyAutoReplyConfiguration => {
  const languages = getAutoReplyLanguages(primaryLanguage, supportedLanguages);
  const emptyConfiguration: WarrantyAutoReplyConfiguration = {
    messages: languages.map((language) => ({ language, message: "" })),
  };

  if (!value.trim()) {
    return emptyConfiguration;
  }

  try {
    const parsed = JSON.parse(value) as WarrantyAutoReplyConfiguration;
    const messageByLanguage = new Map(parsed.messages.map((entry) => [entry.language, entry.message]));
    return {
      messages: languages.map((language) => ({
        language,
        message: messageByLanguage.get(language) ?? "",
      })),
    };
  } catch (error) {
    console.error("Failed to parse warranty auto-reply configuration:", error);
    return emptyConfiguration;
  }
};

const serializeAutoReplyConfiguration = (configuration: WarrantyAutoReplyConfiguration) => {
  return JSON.stringify({
    messages: configuration.messages
      .map((entry) => ({
        language: entry.language,
        message: entry.message.trim(),
      }))
      .filter((entry) => entry.message.length > 0),
  });
};

const formatLanguageLabel = (language: string) => language.toUpperCase();

export function WarrantySettings({
  buildingConfiguration,
  buildingId,
  primaryLanguage,
  supportedLanguages,
  refreshBuildingConfiguration,
}: WarrantySettingsProps) {
  const { t } = useTranslation();
  const { user } = useOutletContext<{ user: UserInfo }>();
  const isDobuStaff = isDobuEmployee(user);
  const { mutateAsync: updateConfiguration } = useUpdateConfiguration();
  const [saveResult, setSaveResult] = useState<ApiResponse<unknown> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorState>(new Set());

  const getConfigValue = useCallback(
    (type: EntityConfigurationType): string => {
      const config = buildingConfiguration?.find((conf) => conf.type === type);
      return config?.value || "";
    },
    [buildingConfiguration],
  );

  const initialFormData = useMemo<WarrantyNotificationFormData>(
    () => ({
      [EntityConfigurationType.WARRANTY_PROMPT]: getConfigValue(EntityConfigurationType.WARRANTY_PROMPT),
      [EntityConfigurationType.FILE_UPLOAD_MANDATORY]:
        getConfigValue(EntityConfigurationType.FILE_UPLOAD_MANDATORY) === "true",
      [EntityConfigurationType.WARRANTY_DISABLED_MESSAGE]: getConfigValue(
        EntityConfigurationType.WARRANTY_DISABLED_MESSAGE,
      ),
      [EntityConfigurationType.WARRANTY_SYNC_EMAIL]: getConfigValue(EntityConfigurationType.WARRANTY_SYNC_EMAIL),
      [EntityConfigurationType.CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE]: getConfigValue(
        EntityConfigurationType.CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE,
      ),
      [EntityConfigurationType.WARRANTY_EMAIL_USER_LIST]: getConfigValue(
        EntityConfigurationType.WARRANTY_EMAIL_USER_LIST,
      ),
    }),
    [getConfigValue],
  );

  const initialAutoReplyConfiguration = useMemo(
    () =>
      parseAutoReplyConfiguration(
        getConfigValue(EntityConfigurationType.WARRANTY_AUTO_REPLY_MESSAGE),
        primaryLanguage,
        supportedLanguages,
      ),
    [getConfigValue, primaryLanguage, supportedLanguages],
  );

  const [formData, setFormData] = useState<WarrantyNotificationFormData>(initialFormData);
  const [autoReplyConfiguration, setAutoReplyConfiguration] =
    useState<WarrantyAutoReplyConfiguration>(initialAutoReplyConfiguration);

  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  useEffect(() => {
    setAutoReplyConfiguration(initialAutoReplyConfiguration);
  }, [initialAutoReplyConfiguration]);

  const handleInputChange = (configurationType: EntityConfigurationType, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [configurationType]: value,
    }));
    // Clear error for this field when user edits it
    if (fieldErrors.has(configurationType)) {
      setFieldErrors((prev) => {
        const newErrors = new Set(prev);
        newErrors.delete(configurationType);
        return newErrors;
      });
    }
  };

  const handleAutoReplyChange = (language: string, message: string) => {
    setAutoReplyConfiguration((prev) => ({
      messages: prev.messages.map((entry) => (entry.language === language ? { ...entry, message } : entry)),
    }));

    if (fieldErrors.has(EntityConfigurationType.WARRANTY_AUTO_REPLY_MESSAGE)) {
      setFieldErrors((prev) => {
        const newErrors = new Set(prev);
        newErrors.delete(EntityConfigurationType.WARRANTY_AUTO_REPLY_MESSAGE);
        return newErrors;
      });
    }
  };

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveResult(null);
    setFieldErrors(new Set());

    const updates: Array<{
      type: EntityConfigurationType;
      value: string;
    }> = [
      {
        type: EntityConfigurationType.WARRANTY_PROMPT,
        value: formData[EntityConfigurationType.WARRANTY_PROMPT],
      },
      {
        type: EntityConfigurationType.FILE_UPLOAD_MANDATORY,
        value: formData[EntityConfigurationType.FILE_UPLOAD_MANDATORY] ? "true" : "false",
      },
      {
        type: EntityConfigurationType.WARRANTY_DISABLED_MESSAGE,
        value: formData[EntityConfigurationType.WARRANTY_DISABLED_MESSAGE],
      },
      {
        type: EntityConfigurationType.WARRANTY_AUTO_REPLY_MESSAGE,
        value: serializeAutoReplyConfiguration(autoReplyConfiguration),
      },
    ];

    if (isDobuStaff) {
      updates.push(
        {
          type: EntityConfigurationType.WARRANTY_SYNC_EMAIL,
          value: formData[EntityConfigurationType.WARRANTY_SYNC_EMAIL],
        },
        {
          type: EntityConfigurationType.CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE,
          value: formData[EntityConfigurationType.CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE],
        },
        {
          type: EntityConfigurationType.WARRANTY_EMAIL_USER_LIST,
          value: formData[EntityConfigurationType.WARRANTY_EMAIL_USER_LIST],
        },
      );
    }

    const results = await Promise.allSettled(
      updates.map((update) =>
        updateConfiguration({
          entityType: EntityConfigurationEntityType.BUILDING,
          buildingId: buildingId,
          configurationType: update.type,
          configurationValue: update.value,
        }).catch((error) => {
          console.error(`Failed to update ${update.type}:`, error);
          throw error;
        }),
      ),
    );

    const newFieldErrors = new Set<EntityConfigurationType>();
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        newFieldErrors.add(updates[index].type);
      }
    });

    const allSuccess = results.every((result) => result.status === "fulfilled");

    setIsSaving(false);
    setFieldErrors(newFieldErrors);

    if (allSuccess) {
      setSaveResult({
        success: true,
        result: null,
      });
      refreshBuildingConfiguration();
    } else {
      setSaveResult({
        success: false,
        result: undefined,
        message: t("buildingSettings.warranty.saveError"),
      });
    }

    setTimeout(() => setSaveResult(null), 10000);
  }, [autoReplyConfiguration, formData, buildingId, updateConfiguration, t, refreshBuildingConfiguration, isDobuStaff]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <Field hasError={fieldErrors.has(EntityConfigurationType.WARRANTY_PROMPT)} id="warranty-prompt">
          <Field.Label>{t("building.warrantyRules")}</Field.Label>
          <TextArea
            value={formData[EntityConfigurationType.WARRANTY_PROMPT]}
            onChange={(e) => handleInputChange(EntityConfigurationType.WARRANTY_PROMPT, e.target.value)}
            disabled={isSaving}
            rows={6}
          />
          <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
        </Field>

        <Field id="file-upload-mandatory" hasError={fieldErrors.has(EntityConfigurationType.FILE_UPLOAD_MANDATORY)}>
          <Checkbox
            label={t("building.fileUploadMandatory")}
            checked={formData[EntityConfigurationType.FILE_UPLOAD_MANDATORY]}
            onChange={(e) => handleInputChange(EntityConfigurationType.FILE_UPLOAD_MANDATORY, e.target.checked)}
            disabled={isSaving}
          />
          <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
        </Field>

        <Field
          hasError={fieldErrors.has(EntityConfigurationType.WARRANTY_DISABLED_MESSAGE)}
          id="warranty-disabled-message"
        >
          <Field.Label>{t("building.warrantyDisabledMessage")}</Field.Label>
          <TextArea
            value={formData[EntityConfigurationType.WARRANTY_DISABLED_MESSAGE]}
            onChange={(e) => handleInputChange(EntityConfigurationType.WARRANTY_DISABLED_MESSAGE, e.target.value)}
            disabled={isSaving}
            rows={3}
            placeholder={t("warranty.submissionDisabled")}
          />
          <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
        </Field>

        <Field
          hasError={fieldErrors.has(EntityConfigurationType.WARRANTY_AUTO_REPLY_MESSAGE)}
          id="warranty-auto-reply-message"
        >
          <Field.Label>{t("building.warrantyAutoReplyMessage")}</Field.Label>
          <div className="mt-2 space-y-3">
            {autoReplyConfiguration.messages.map((entry) => (
              <div key={entry.language} className="rounded-md border border-gray-200 bg-slate-50 p-3">
                <div className="mb-2 text-sm font-medium text-gray-600">{formatLanguageLabel(entry.language)}</div>
                <TextArea
                  value={entry.message}
                  onChange={(e) => handleAutoReplyChange(entry.language, e.target.value)}
                  disabled={isSaving}
                  rows={4}
                />
              </div>
            ))}
          </div>
          <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
        </Field>

        {isDobuStaff && (
          <>
            <Field hasError={fieldErrors.has(EntityConfigurationType.WARRANTY_SYNC_EMAIL)} id="warranty-sync-email">
              <Field.Label>{t("building.warrantySyncEmail")}</Field.Label>
              <Input
                type="text"
                value={formData[EntityConfigurationType.WARRANTY_SYNC_EMAIL]}
                onChange={(e) => handleInputChange(EntityConfigurationType.WARRANTY_SYNC_EMAIL, e.target.value)}
                disabled={isSaving}
              />
              <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
            </Field>

            <Field
              hasError={fieldErrors.has(EntityConfigurationType.CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE)}
              id="custom-warranty-notification-email-template"
            >
              <Field.Label>{t("building.customWarrantyNotificationEmailTemplate")}</Field.Label>
              <TextArea
                value={formData[EntityConfigurationType.CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE]}
                onChange={(e) =>
                  handleInputChange(EntityConfigurationType.CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE, e.target.value)
                }
                disabled={isSaving}
                rows={6}
              />
              <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
            </Field>

            <Field
              hasError={fieldErrors.has(EntityConfigurationType.WARRANTY_EMAIL_USER_LIST)}
              id="warranty-email-user-list"
            >
              <Field.Label>{t("building.warrantyEmailUserList")}</Field.Label>
              <TextArea
                value={formData[EntityConfigurationType.WARRANTY_EMAIL_USER_LIST]}
                onChange={(e) => handleInputChange(EntityConfigurationType.WARRANTY_EMAIL_USER_LIST, e.target.value)}
                disabled={isSaving}
                rows={6}
              />
              <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
            </Field>
          </>
        )}

        <div className="flex items-center gap-4">
          <Button onClick={handleSave} disabled={isSaving} loading={isSaving}>
            {isSaving ? t("settings.saving") : t("settings.save")}
          </Button>
          <div className="flex-grow">
            {saveResult && (
              <ApiBanner apiResponse={saveResult} successMessage={t("buildingSettings.warranty.saveSuccess")} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
